import './styles.scss';
import React, {Dispatch, useCallback, useEffect, useRef, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import {
  TAdvConstraint,
  TAdvSearchDate, TConstraint,
  TQueryItem
} from "../../../constants";

import SearchUtils from "../../../utils/search-utils";
import {ConstraintDropdownContext, TFilterOption} from "../../../types/search";

/* Bootstrap */
import {Button, Col, Container, InputGroup, Row} from "react-bootstrap";
import Form from "react-bootstrap/Form";
import Collapse from 'react-bootstrap/Collapse';

/* Redux */
import {ISearchDate, ISearchState} from "../../../types/redux/search";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {
  addFilters,
  addRemoveSelectedFilters, resetFilters,
  removeFilters,
  setQuery,
  setSearchDates, suspendSearch, setCurrentPage
} from "../../../redux/slices/search-slice";
import {AnyAction} from "@reduxjs/toolkit";


/* Components */
import ConstraintDropdown from "./constraint-dropdown";
import DatePicker from "./date-picker";
import SearchHistoryDropdown from "../search-input/search-history-dropdown";

/* hooks */
import useInsertDBFields from "../hooks/useInsertDBFields";
import usePlatform from "../../../hooks/usePlatform";
import FiltersCheck from "../../filters/filters-check";
import {FormCheckType} from "react-bootstrap/FormCheck";
import Utils from "../../../utils";
import {clearResults} from "../../../redux/slices/result-slice";

const SearchAdvanced = ():JSX.Element => {
  const searchState: ISearchState = useSelector<RootState, ISearchState>(state => state.search);
  const dispatch:Dispatch<AnyAction> = useDispatch();

  const [searchParams, setSearchParams] = useState<URLSearchParams>(new URLSearchParams(window.location.search))

  const [constraints, setConstraints] = useState<TAdvConstraint[]>([{...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT}]);
  const [dates, setDates] = useState<TAdvSearchDate[]>([...Constants.ADV_SEARCH_DATES]);
  const [closePickers, setClosePickers] = useState<boolean>(false);
  const [dirty, setDirty] = useState<boolean>(false);
  const [minimized, setMinimized] = useState<boolean>(false);

  const insertDBFields = useInsertDBFields();
  const isDesktop: boolean = usePlatform().isDesktop;

  const headingRef = useRef(null);
  const [headerWidth, setHeaderWidth] = useState<number>(0);
  const componentRef = useRef(null);

  useEffect(()=>{
    const resized = () =>  setHeaderWidth(headingRef.current?.clientWidth)
    resized();
    window.addEventListener(Constants.RESIZE, (event) => (resized()), true);
    return ()=> window.removeEventListener(Constants.RESIZE, (event) => resized(), true);
  }, [])

  useEffect(()=>{
    setSearchParams(new URLSearchParams(window.location.search))
    // eslint-disable-next-line
  },[window.location.search]);

  useEffect(() => {
    let queryParams: string | null = '';
    const searchQuery: TQueryItem[] = searchState.query;
    for (let i=0; i<searchQuery.length; i++) {
      const qp:TQueryItem = searchQuery[i];
      queryParams += `${qp.operator},${qp.field},${qp.match},${qp.term}` + (i < searchQuery.length - 1 ? ';' : '')
    }

    const constraintsFromParams: TAdvConstraint[] = SearchUtils.searchParamsToConstraints(queryParams);
    setConstraints(constraintsFromParams);
    constraintsFromParams.forEach(c=> {
      if (c.term && c.term.length > 0) {
        setDirty(true)
      }
    });

    /* !note: search-slice capture dates from params independently, async redux results in visible redraw */
    const dateParams: string | null = searchParams.get(Constants.DATES_KEY);
    setDates(SearchUtils.dateParamToDates(dateParams));

    // eslint-disable-next-line
  }, [searchState.query, searchState.dates,  searchParams])

  useEffect(()=>{
    const closeCalendar = (e:any):void=> {
      /* close any calendar that is open if click outside of it */
      let found:boolean = false;
      const iterator = e.target.parentElement.classList.values();

      for (const value of iterator) {
        if (value.includes('react-calendar') || value.includes('date-field-input') || value.includes('date-field-btn')) {
          found = true;
          break
        }
      }

      if (!found) {
        setClosePickers(true);
      }
    }
    document.addEventListener(Constants.MOUSEDOWN, e=> closeCalendar(e));

    return () => document.removeEventListener(Constants.MOUSEDOWN, e=> closeCalendar(e));
  }, [constraints])

  const updateConstraints = (line: number, field: string, value: string | null): void => {
    const constraintClone: any = [...constraints]
    if (field === Constants.TERM) {
      constraintClone[line][field] = value;
    } else {
      let entry: any | null = Constants.ADV_SEARCH_DROPDOWNS[field].find((entry: TConstraint)=>{
        return entry.value === value;
      });

      /* Special case - search fields are broken out separately */
      if (!entry && (field === Constants.FIELD)) {
        entry = Constants.ADV_SEARCH_DROPDOWNS[Constants.FIELD_SEARCH].find((entry: TConstraint)=>{
          return entry.value === value;
        });
      }
      constraintClone[line][field] = {...entry};
    }

    setConstraints(constraintClone);

    setDirty(true);
  }

  const addNewLine = ()=> {
    const constraintClone: any = [...constraints]
    constraintClone.push({...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT});
    setConstraints(constraintClone);

    setDirty(true);
  }

  const updateDates = (date: any): void => {
    const pos: number = dates.findIndex((entry: any) => {
      return entry.id === date.id
    });

    const updatedDates: any[] = [...dates];
    updatedDates[pos] = {...date};
    setDates(updatedDates);

    setDirty(true);
  }

  const clear = (): void => {
    setClosePickers(true)
    setConstraints([{...Constants.ADV_SEARCH_DEFAULT_CONSTRAINT}]);
    setDates([...Constants.ADV_SEARCH_DATES]);

    /* reset params */
    searchParams.delete(Constants.QUERY_KEY);
    searchParams.delete(Constants.FILTERS_KEY);
    searchParams.delete(Constants.DATES_KEY);
    window.history.replaceState(null, '', window.location.pathname + "?" + searchParams.toString());

    setDirty(false);
  }

  const performQuery = (e:React.MouseEvent):void => {
    e.preventDefault();
    e.stopPropagation();

    const query: TQueryItem[] = [];
    for (let i=0; i<constraints.length; i++) {
      const queryItem: TQueryItem = {...Constants.DEFAULT_SEARCH};
      queryItem.operator = (i > 0 ? constraints[i].operator.value : null); /* first constraint no operator */
      queryItem.field = constraints[i].field.value;
      queryItem.match = constraints[i].match.value;
      queryItem.term = constraints[i].term ? constraints[i].term : '*:*';
      query.push(queryItem);
    }

    /* Query */
    searchParams.set(Constants.QUERY_KEY, SearchUtils.searchQueryToString(query));
    dispatch(setQuery({
      query: query,
      recent_query: true
    }))

    /* Dates */
    const searchDates: ISearchDate[] = [];

    for (let i=0; i<dates.length; i++) {
      const date: TAdvSearchDate = dates[i];

      if ((date.start) || (date.end)) {
        searchDates.push({
          id:date.id,
          field:date.field,
          from: date.start,
          to: date.end
        })
      }
    }
    dispatch(setSearchDates(searchDates));

    /* date params */
    if (searchDates.length > 0) {
      const datesParams: string = searchDates.map((date:ISearchDate) =>{
        return date.id + ',' + date.field + ',' + date.from + ',' + date.to
      }).join(';');
      searchParams.set(Constants.DATES_KEY, datesParams);
    } else {
      searchParams.delete(Constants.DATES_KEY);
    }

    /* Exclude filter may be applied */
    if (searchState.applied_filters.length > 0) {
      let filtersString: string = '';
      searchState.applied_filters.forEach(f=>filtersString +=`${f.id},`);
      searchParams.set(Constants.FILTERS_KEY, filtersString.substring(0,filtersString.length - 1))
    }
    searchParams.set(Constants.DB_SET_KEY, searchState.db_set);
    searchParams.set(Constants.SORT_KEY, searchState.sort);
    searchParams.set(Constants.PAGE_KEY, '1');
    searchParams.set(Constants.RESULTS_PER_PAGE_KEY, String(searchState.results_per_page));
    searchParams.set(Constants.INDUSTRY_KEY, searchState.industry);

    const searchStateParams: URLSearchParams = SearchUtils.searchQueryToUrlSearchParams(searchState);
    if ((searchStateParams.get(Constants.QUERY_KEY) !== searchParams.get(Constants.QUERY_KEY)) ||
          (searchStateParams.get(Constants.FILTERS_KEY) !== searchParams.get(Constants.FILTERS_KEY))) {
      dispatch(clearResults());
      dispatch(resetFilters());
    }
    window.history.replaceState(null, '', window.location.pathname + "?"  + searchParams.toString());

    dispatch(suspendSearch(false));
    dispatch(setCurrentPage(1));

    /* minimize on search */
    setMinimized(true)
  }

  useEffect(()=>{
    const handleKeyup = (e:any): void => {
      if (e.key === Constants.ENTER) {
        performQuery(e)
      }
    }
    document.removeEventListener(Constants.KEYUP, e=> handleKeyup(e));
    document.addEventListener(Constants.KEYUP, e=> handleKeyup(e));

    return () => document.removeEventListener(Constants.KEYUP, e=> handleKeyup(e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[constraints])

 /* const hasTerm = ():boolean => {
    return constraints.find((constraint: TAdvConstraint) => (constraint.term && (constraint.term.length > 0))) !== undefined;
  }*/

  const removeConstraintLine = (line: number): void => {
    const constraintsCln: TAdvConstraint[] = [...constraints];
    constraintsCln.splice(line,1);
    setConstraints(constraintsCln);
  }

  const buildConstraint = useCallback((line: number):JSX.Element => {
    return (
      <ConstraintDropdownContext.Provider
        key={'cs' + line}
        value={{line: line, updateConstraints: updateConstraints}}
      >
        <Row className={'constraint-row mb-lg-3'}>
          {/* first line not include operator */}
          <Col className={'col-1'}>{(line > 0) &&
            <ConstraintDropdown
              constraint={Constants.OPERATOR}
              selected={constraints[line].operator.label || ''}
            />}
          </Col>

          <Col className={(isDesktop?'col-3':'')}>
            <ConstraintDropdown
              constraint={Constants.FIELD}
              selected={constraints[line].field.label}
            />
          </Col>

          <Col className={(isDesktop?'col-2-5':'')}>
            <ConstraintDropdown
              constraint={Constants.MATCH}
              selected={constraints[line].match.label || ''}
            />
          </Col>

          <Col>
            <InputGroup>
              <Form.Control
                className={'border-0 rounded px-2 ms-1'}
                type={'text'}
                aria-label={'term'}
                aria-describedby={'term-input'}
                placeholder={'Enter a search term'}
                value={constraints[line].term || ''}
                onChange={e=> updateConstraints(line, Constants.TERM, insertDBFields.insertField(e.target.value).str)}
              />
              <div className={'underline'}/>
            </InputGroup>
          </Col>

          <div className={'col-min'}>
           {(line > 0) && <Button
             className={'clear-btn btn-transparent border-0 px-1 py-0 '}
             onClick={e=> removeConstraintLine(line)}
             >
              <i className={'bi bi-x text-black'}/>
             </Button>}
          </div>
        </Row>
      </ConstraintDropdownContext.Provider>
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [constraints, updateConstraints, removeConstraintLine]);

  const handleDisplayFilterChange = (filterOption: TFilterOption)=>{
    const isApplied: boolean = (searchState.selected_filters_ids.findIndex(id => (id === filterOption.id)) !== -1);
    if (isApplied) {
       dispatch(removeFilters([filterOption]));
    } else {
      dispatch(addFilters([filterOption]));
    }
    dispatch(addRemoveSelectedFilters({ids: [filterOption.id], method: isApplied ?  Constants.REMOVE : Constants.ADD } ));
  }

  return (
    <Container ref={componentRef} className={'search-advanced justify-content-start p-0 pb-3 border-start border-end border-bottom rounded-bottom rounded-end'}>
      {/* heading */}
      <Row ref={headingRef} className={'heading bg-light mx-0 border-top border-left border-bottom rounded-end d-flex justify-content-between position-relative'}>
        <div className={'header text-large w-auto'}><strong>Advanced Search</strong></div>
        <div className={'d-flex w-auto px-0'}>

          <div className={'position-absolute '} style={{ width: headerWidth, left: 0 }}>
            <SearchHistoryDropdown/>
          </div>

          <Button
            className={'minimize-btn btn-transparent border-0 rounded-0 text-black py-2'}
            onClick={() => setMinimized(!minimized)}
            aria-controls="example-collapse-text"
            aria-expanded={minimized}
          >
            {minimized && <i className={'bi bi-chevron-down'}/>}
            {!minimized && <i className={'bi bi-chevron-up'}/>}
          </Button>
        </div>
      </Row>

      <Collapse in={!minimized}>
        <div>
          <Row className={'m-0 mt-4 d-flex flex-nowrap'}>
            <div className={'header w-auto pe-1 text-smaller ms-1'}><strong>Exclude:</strong></div>
            <ul className={'filter-options d-flex'}>
              {searchState.filter_display?.options.map((filterOption:TFilterOption, index:number) => {
                return <li className={'list-group me-4 text-smaller'} key={filterOption.id + '_fc'}>
                  <FiltersCheck
                    onHandleChange={() => handleDisplayFilterChange({...filterOption})}
                    type={Constants.CHECKBOX as FormCheckType}
                    filterOption={filterOption}/>
                </li>
              })}
            </ul>
          </Row>

          <hr className={'mx-3 my-0'}/>

          {/* Constraints */}
          <Row className={'my-4 mx-2 text-smaller'}>
            <Col lg={9} className={'mt-2'}>
              <Row className={'mb-2'}>
                <div><strong>Build your search:</strong></div>
              </Row>

              <Row>
                {/* Constraint dropdowns */}
                {constraints.map((constraint:any, index: number) => buildConstraint(index))}
              </Row>

              <Row className={'mx-0 mt-2'}>
                {/* Add new constraint line */}
                <Button
                  variant={'light text-primary text-smaller border w-auto py-2 pe-3 me-3'}
                  onClick={()=>addNewLine()}
                >
                  <i className={'bi bi-plus me-1'}/>
                  Add New Line
                </Button>

                {/* Clear All */}
                {<Button
                  className={'light text-smaller border w-auto py-2 pe-3 ' + (dirty ? ' text-primary' : 'text-secondary')}
                  disabled={!dirty}
                  variant={'light'}
                  onClick={()=>clear()}>
                  <i className={'bi bi-x me-1'}/>
                  Reset
                </Button>}
              </Row>
            </Col>

            {/* Date Ranges */}
            <Col lg={3} className={'date-ranges pe-2 mt-2'}>
             <div className={'mb-0 text-smaller'}><strong>Date Ranges</strong></div>

            {/* Date pickers - some dependent on industries */}
            {dates.map((picker: TAdvSearchDate) => ((picker.industry === Constants.ALL_INDUSTRIES) ||
              (picker.industry === searchState.industry)) &&
                <DatePicker
                  key={picker.id}
                  data={{...picker}}
                  updateDates={updateDates}
                  closePickers={closePickers}
                  setClosePickers={setClosePickers}
                />
              )}
            </Col>
          </Row>
        </div>
      </Collapse>

      <Row className={'z-3 justify-content-end mx-0 px-3 mt-2 position-relative'}>
        <div className={'z-3 position-relative bottom-0 start-0'}>
          {constraints.map((constraint:TAdvConstraint,i: number) => {
            if (constraint.term) {
              return (
                <div key={'constr_' + i}>
                  <i className={'bi bi-arrow-return-right text-primary mx-2'}/>
                  <span>{constraint.operator.value && (i>0) ? constraint.operator.label + ' ' : '' }
                    {constraint.field.value !== 'all' ? 'field ' + Utils.toTitleCase(constraint.field.label) + ' ': 'All Fields '}
                    that {constraint.match.label.toLowerCase() + ' ' + (constraint.match.value === 'contains-exact' ? ' match ' : ' ') }
                    <strong>{constraint.term}</strong>
                  </span>
                </div>
              )
            }
            return <></>
          })}
        </div>

        <Button
          className={'search-btn z-3 submit-btn w-auto py-2 px-4 position-relative'}
          variant={'primary'}
          //disabled={!dirty}
          disabled={false}
          onClick={e=>performQuery(e)}
        >
          <i className="bi bi-search me-2"/>
          Search
        </Button>
      </Row>

    </Container>
  )
}
export default SearchAdvanced;
